// src/components/Billing/ContentfulPricingSection.js
// @flow
import React, { useState } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import { log } from '../../utils/jsUtils';
import { createClasses } from '../../utils/stylesUtils';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import { Typography, Hidden, Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';

import contentfulPricingSectionStyles from './ContentfulPricingSection.Style';

type PlanType = {
  id: string,
  title: string,
  ctaLabel: string,
  subTitle?: string,
  monthly?: number|string,
  yearly?: number|string,
  price?: string,
  linkMonthly?: string,
  linkYearly?: string,
  link?: string,
  planId?: string,
  monthlyPlanId?: string,
  yearlyPlanId?: string
};

type FeatureType = {
  label: string,
  plans: Array<string>
};

type MobilePlanFeature = {
  label: string,
  description: string
};

type MobilePlanType = {
  planId: string,
  features: Array<MobilePlanFeature>
};

type PricingTableType = {
  enableMonthYearSwitch: boolean,
  showOnlyMobilePlans: boolean,
  plans: Array<PlanType>,
  features: Array<FeatureType>,
  plansMobile?: Array<MobilePlanType>
};

type Props = {
  selectPlan: (planId: string) => void,
  pricingTable: PricingTableType,
  section: any
};

const ContentfulPricingSection = (props: Props) => {
  const { pricingTable, selectPlan, section } = props;
  const theme = useTheme();
  log('[ContentfulPricingSection] section: ', section);

  // Build all classes with a single function call, including special cases
  const classes = createClasses(
    contentfulPricingSectionStyles, 
    theme, 
    {}, // Default options
    {
      // Special options for specific styles
      mobileOnlyGridPaper: { plansNb: pricingTable?.plans?.length || 1 }
    }
  );
  
  const [isPeriodYearly, setIsPeriodYearly] = useState(true);

  return (
    <>
      <Typography sx={classes.h1} variant="h1">
        Pick a plan
      </Typography>
      <Typography sx={classes.h3} variant="h3">
        Starting at less than $1/day.
      </Typography>

      {pricingTable ? (
        <Grid container sx={classes.container} direction="column">
          {/* Toggle monthly vs. yearly */}
          {pricingTable.enableMonthYearSwitch ? (
            <Grid
              item
              xs={12}
              sx={classes.yearlyToggle}
            >
              <Grid
                alignItems="center"
                component="label"
                container
                spacing={1}
              >
                <Grid item>{'Billed Monthly'}</Grid>
                <Grid item>
                  <Switch
                    checked={isPeriodYearly}
                    color="primary"
                    name="isPeriodYearly"
                    onChange={(e) => setIsPeriodYearly(e.target.checked)}
                  />
                </Grid>
                <Grid item>{'Billed Yearly'}</Grid>
              </Grid>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            {!pricingTable.showOnlyMobilePlans ? (
              <Paper sx={classes.gridPaper} elevation={4}>
                {/* Desktop or comparison table logic */}
                <Hidden only={['xs']}>
                  <Grid container direction="row">
                    {/* left column with features */}
                    <Grid
                      item
                      xs={
                        12 -
                        pricingTable.plans.length *
                          Math.floor(9 / pricingTable.plans.length)
                      }
                    >
                      <Grid container sx={classes.featuresList}>
                        <Grid sx={classes.featureHeader} item />
                        {pricingTable.features.map((feature, featureIndex) => (
                          <Grid
                            sx={{
                              ...classes.gridCell,
                              ...classes.featureDesc,
                              ...(featureIndex === 0 ? classes.emphasisDescr : {}),
                              ...(pricingTable.features.length === featureIndex + 1
                                ? { borderBottom: 0 }
                                : {})
                            }}
                            item
                            key={`feature-${featureIndex}`}
                          >
                            {feature.label}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>

                    {/* plans columns */}
                    {pricingTable.plans.map((plan, planIndex) => {
                      const planDef = R.find(
                        R.propEq('id', plan.id)
                      )(pricingTable.plans);
                      return (
                        <Grid
                          item
                          key={`plan-${planIndex}`}
                          xs={Math.floor(9 / pricingTable.plans.length)}
                        >
                          <Grid container sx={classes.featuresList}>
                            {/* plan header */}
                            <Grid sx={classes.featureHeader} item>
                              <Typography sx={classes.planTitle} variant="h2">
                                {plan.title}
                              </Typography>
                              {/* Price display */}
                              <Typography sx={classes.planPrice} variant="h3">
                                {isPeriodYearly
                                  ? plan.yearly
                                  : plan.monthly}
                              </Typography>
                              {/* Pricing logic */}
                              {/* e.g. if monthly vs. yearly */}
                              <Button
                                sx={classes.topBuy}
                                color="primary"
                                onClick={() =>
                                  selectPlan(
                                    process.env.REACT_APP_ENVF === 'env.development'
                                      ? (isPeriodYearly
                                          ? planDef?.yearlyTestPlanId
                                          : planDef?.monthlyTestPlanId)
                                      : (isPeriodYearly
                                          ? planDef?.yearlyPlanId
                                          : planDef?.monthlyPlanId)
                                  )
                                }
                                variant="contained"
                              >
                                Get Started
                              </Button>
                            </Grid>

                            {/* plan features */}
                            {pricingTable.features.map((feature, featureIndex) => (
                              <Grid
                                sx={{
                                  ...classes.gridCell,
                                  ...classes.featureItem,
                                  ...(featureIndex === 0 ? classes.emphasis : {}),
                                  ...(pricingTable.features.length === featureIndex + 1
                                    ? { borderBottom: 0 }
                                    : {})
                                }}
                                item
                                key={`feature-plan${planIndex}-${featureIndex}`}
                              >
                                {feature.plans?.[planIndex] === 'checked' ? (
                                  <CheckIcon sx={classes.checkIcon} />
                                ) : (
                                  <pre>{feature.plans?.[planIndex]}</pre>
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Hidden>
              </Paper>
            ) : null}

            {/* If showOnlyMobilePlans, display mobile layout */}
            {pricingTable.showOnlyMobilePlans ? (
              <Box sx={classes.mobileOnlyPricingGridContainer}>
                {(pricingTable.plansMobile || []).map((plan, planIndex) => {
                  const planDef = R.find(R.propEq('id', plan.planId))(
                    pricingTable.plans
                  );
                  return (
                    <Paper
                      key={planIndex}
                      sx={classes.mobileOnlyGridPaper}
                      elevation={4}
                    >
                      {/* Mobile plan title and price */}
                      <Typography sx={classes.planTitle} variant="h2">
                        {planDef?.title}
                      </Typography>
                      <Typography sx={classes.planPrice} variant="h3">
                        {isPeriodYearly
                          ? planDef?.yearly
                          : planDef?.monthly}
                      </Typography>
                      <Button
                        sx={classes.topBuy}
                        color="primary"
                        onClick={() =>
                          selectPlan(
                            process.env.REACT_APP_ENVF === 'env.development'
                              ? (isPeriodYearly
                                  ? planDef?.yearlyTestPlanId
                                  : planDef?.monthlyTestPlanId)
                              : (isPeriodYearly
                                  ? planDef?.yearlyPlanId
                                  : planDef?.monthlyPlanId)
                          )
                        }
                        variant="outlined"
                      >
                        {planDef?.ctaLabel || 'Get Started'}
                      </Button>
                    </Paper>
                  );
                })}
              </Box>
            ) : null}
          </Grid>
        </Grid>
      ) : null}
    </>
  );
};

export default ContentfulPricingSection;